// Centralized API configuration
// Dynamically determines the API host based on where the frontend is accessed from
// Cache bust: v2.0

/**
 * Get the API Gateway base URL based on the current host
 * This allows the app to work both on localhost and network IPs
 * Uses the current protocol (http/https) to avoid Mixed Content errors
 */
export const getApiBaseUrl = (): string => {
  // Use relative path to let browser use same protocol as page
  return '/api';
};

/**
 * Get the full API URL for a given endpoint
 * @param endpoint - The API endpoint (e.g., '/api/content/items')
 * @returns The full API URL
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Get the asset URL for media files
 * @param assetPath - The asset path (e.g., '/assets/videos/example.mp4')
 * @returns The full asset URL
 */
export const getAssetUrl = (assetPath: string): string => {
  const baseUrl = getApiBaseUrl();
  // Ensure path starts with /
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${baseUrl}${normalizedPath}`;
};

// Export constants for backward compatibility
export const API_BASE_URL = getApiBaseUrl();

/**
 * Get the Remotion service URL for video assets
 *
 * In development: localhost:8010 (local Remotion service)
 * In production: empty string (videos come with full S3 URLs from Lambda)
 *
 * Note: Lambda-rendered videos return full S3 URLs (https://s3...amazonaws.com/...)
 * which don't need this prefix. The frontend checks if the URL starts with 'http'
 * and uses it directly in that case.
 */
export const getRemotionServiceUrl = (): string => {
  // Check for environment variable override
  if (process.env.REACT_APP_REMOTION_SERVICE_URL) {
    return process.env.REACT_APP_REMOTION_SERVICE_URL;
  }
  // Default to localhost for development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8010';
  }
  // In production, Lambda-rendered videos return full S3 URLs
  // Local renders would need the API Gateway to proxy, but Lambda is preferred
  return '';
};

export const REMOTION_SERVICE_URL = getRemotionServiceUrl();